import { type DocumentNode } from '@apollo/client';
import {
  Button,
  ButtonColor,
  ButtonWidth,
  DateFormat,
  formatDate,
  formatTime,
  Modal,
  ModalClose,
  ModalContent,
  type ModalHook,
  ModalSize,
  Tag,
  TagColor,
  TagSize,
  ToastDuration,
  ToastType,
  useControlledModalState,
  useToast,
} from '@leland-dev/leland-ui-library';
import React, { type FC, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { CoachingSessionType } from '../../../../__generated-gql-types__/globalTypes';
import { DEFAULT_ERROR_MESSAGE } from '../../../../utils/constants';
import { wasMutationSuccessful } from '../../../../utils/mutations';
import { ErrorMessage, Textarea } from '../../../forms/Inputs';

import {
  ApplyRecurringSessionChanges,
  SelectApplyRecurringSessionChanges,
} from './SelectApplyRecurringSessionChanges';
import {
  type CancelCoachingSessionModalCancelCoachingSessionMutationVariables,
  type CancelCoachingSessionModalCoachingSessionFragment,
  useCancelCoachingSessionModalCancelCoachingSessionMutation,
} from './__generated-gql-types__/CancelCoachingSessionModal.generated';

interface CancelCoachingSessionModalHookProps {
  session: CancelCoachingSessionModalCoachingSessionFragment;
  refetchQueries?: DocumentNode[];
}

interface CancelCoachingSessionModalProps
  extends CancelCoachingSessionModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const CancelCoachingSessionModalContent: FC<
  CancelCoachingSessionModalProps
> = ({ open, setOpen, session, refetchQueries }) => {
  const { showToast } = useToast();

  const [cancelCoachingSession, { loading: cancelCoachingSessionInFlight }] =
    useCancelCoachingSessionModalCancelCoachingSessionMutation();

  const onSubmit = async (
    values: CancelCoachingSessionModalCancelCoachingSessionMutationVariables,
    setErrorMessage: (errorMessage: string | null) => void,
  ) => {
    setErrorMessage(null);

    try {
      const res = await cancelCoachingSession({
        variables: {
          id: session.id,
          message: values.message,
          cancelFutureSessions: values.cancelFutureSessions,
        },
        refetchQueries,
      });

      const sessionTitle = session.name;

      if (wasMutationSuccessful(res)) {
        setOpen(false);
        showToast({
          type: ToastType.SUCCESS,
          duration: ToastDuration.NORMAL,
          message: `Session "${sessionTitle}" canceled.`,
        });
      } else {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
      }
    } catch (e) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
      console.warn(e);
    }
  };

  return (
    <Modal open={open} onOpenChange={setOpen}>
      {session.recurringSession ? (
        <CancelCoachingSessionRecurringSessionModalContent
          session={session}
          onSubmit={onSubmit}
          disabled={cancelCoachingSessionInFlight}
        />
      ) : (
        <CancelCoachingSessionMainModalContent
          session={session}
          onSubmit={onSubmit}
          disabled={cancelCoachingSessionInFlight}
        />
      )}
    </Modal>
  );
};

type CancelCoachingSessionModalContentProps = Pick<
  CancelCoachingSessionModalProps,
  'session'
> & {
  onSubmit: (
    values: CancelCoachingSessionModalCancelCoachingSessionMutationVariables,
    setErrorMessage: (errorMessage: string | null) => void,
  ) => void;
  disabled: boolean;
};

const CancelCoachingSessionMainModalContent: FC<
  CancelCoachingSessionModalContentProps
> = ({ session, onSubmit, disabled }) => {
  const { register, handleSubmit } =
    useForm<CancelCoachingSessionModalCancelCoachingSessionMutationVariables>({
      defaultValues: {
        cancelFutureSessions: false,
      },
    });

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);

  const dateAndTime = `${formatDate(
    session.startAt,
    DateFormat.LONG_MONTH_DAY,
  )} @ ${formatTime(session.startAt)}`;

  const onSubmitWrapper: SubmitHandler<
    CancelCoachingSessionModalCancelCoachingSessionMutationVariables
  > = (values) => {
    return onSubmit(values, setErrorMessage);
  };

  return (
    <ModalContent
      header="Cancel Session"
      footer={
        <div className="p-2">
          <div className="flex w-full items-center justify-between space-x-2">
            <ModalClose asChild>
              <Button
                label="Back"
                buttonColor={ButtonColor.WHITE}
                width={ButtonWidth.FULL}
              />
            </ModalClose>
            <Button
              form="cancel-coaching-session-form"
              type="submit"
              label="Cancel Session"
              buttonColor={ButtonColor.PRIMARY}
              width={ButtonWidth.FULL}
              disabled={disabled}
            />
          </div>
          {errorMessage ? <ErrorMessage errorMessage={errorMessage} /> : null}
        </div>
      }
      size={ModalSize.SMALL}
      preventScroll
    >
      <div className="p-6">
        <div className="flex flex-col items-center text-center">
          <h2 className="text-2xl font-medium text-leland-gray-dark">
            Are you sure?
          </h2>
          <p className="mt-2 text-lg leading-snug text-leland-gray-light">
            Double-check that this is the session you want to cancel before
            proceeding to cancel it.
          </p>
          {dateAndTime ? (
            <>
              <h3 className="mt-6 text-lg font-medium text-leland-gray-dark">
                Scheduled time:
              </h3>
              <Tag
                text={dateAndTime}
                tagColor={TagColor.BLUE}
                size={TagSize.LARGE}
              />
            </>
          ) : null}
        </div>
        <form
          id="cancel-coaching-session-form"
          onSubmit={handleSubmit(onSubmitWrapper)}
        >
          {session.sessionType === CoachingSessionType.ONE_ON_ONE ? (
            <>
              <hr className="my-6" />
              <Textarea
                label={`Send a message to ${session.coach.user.firstName}`}
                flag="Optional"
                placeholder="Type your message here..."
                rows={4}
                register={register('message')}
              />
            </>
          ) : null}
        </form>
      </div>
    </ModalContent>
  );
};

const CancelCoachingSessionRecurringSessionModalContent: FC<
  CancelCoachingSessionModalContentProps
> = ({ onSubmit, disabled }) => {
  const { setValue, watch, handleSubmit } =
    useForm<CancelCoachingSessionModalCancelCoachingSessionMutationVariables>({
      defaultValues: {
        cancelFutureSessions: false,
      },
    });
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);

  const onSubmitWrapper: SubmitHandler<
    CancelCoachingSessionModalCancelCoachingSessionMutationVariables
  > = (values) => {
    return onSubmit(values, setErrorMessage);
  };

  return (
    <ModalContent
      header=""
      footer={
        <div className="p-2">
          <div className="flex w-full items-center justify-between space-x-2">
            <ModalClose asChild>
              <Button label="Back" buttonColor={ButtonColor.WHITE} />
            </ModalClose>
            <Button
              form="cancel-coaching-session-form"
              type="submit"
              label="Okay"
              buttonColor={ButtonColor.PRIMARY}
              disabled={disabled}
            />
            {errorMessage ? <ErrorMessage errorMessage={errorMessage} /> : null}
          </div>
        </div>
      }
      size={ModalSize.SMALL}
      preventScroll
    >
      <div className="p-6">
        <h2 className="text-2xl font-medium">Cancel Session</h2>
        <p className="mb-4 text-lg text-leland-gray-light">
          This is a recurring session. Select an option to cancel:
        </p>
        <form
          id="cancel-coaching-session-form"
          onSubmit={handleSubmit(onSubmitWrapper)}
        >
          <SelectApplyRecurringSessionChanges
            onChange={(value) => {
              setValue(
                'cancelFutureSessions',
                value === ApplyRecurringSessionChanges.ALL_SESSIONS,
              );
            }}
            value={
              watch('cancelFutureSessions')
                ? ApplyRecurringSessionChanges.ALL_SESSIONS
                : ApplyRecurringSessionChanges.JUST_THIS_SESSION
            }
          />
        </form>
      </div>
    </ModalContent>
  );
};

export const useCancelCoachingSessionModal: ModalHook<
  CancelCoachingSessionModalHookProps
> = () => {
  const [open, setOpen] = useControlledModalState();
  const CancelCoachingSessionModal: FC<CancelCoachingSessionModalHookProps> = (
    props,
  ) => {
    return open ? (
      <CancelCoachingSessionModalContent
        {...props}
        open={open}
        setOpen={setOpen}
      />
    ) : null;
  };
  return { open, setOpen, Modal: CancelCoachingSessionModal };
};
